import React, { useState, useEffect } from "react";
import {
  Container, Box, Typography, Button, Grid, Card, CardContent,
  CardActions, CircularProgress, Chip, Alert, Snackbar, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Switch
} from "@mui/material";
import { api } from "../context/AuthContext";
import { Pagination } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom"; // 1. Import useLocation
import { Edit, Delete, People, Add } from "@mui/icons-material";

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

function MyJobs() {
const location = useLocation(); // 2. Initialize location hook
const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const JOBS_PER_PAGE = 10;
  const FIFTEEN_DAYS_MS = 1296000000; // 15 days in milliseconds

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const [openEdit, setOpenEdit] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    company_name: "",
    location: "",
    salary: "",
    description: "",
    requirements: ""
  });
  // 3. EFFECT: Listen for navigation state from Dashboard
    useEffect(() => {
      if (location.state && location.state.activeTab !== undefined) {
        setTabValue(location.state.activeTab);
        setPage(1); // Reset pagination when switching tabs via dashboard
      }
    }, [location.state]);

  // 1. Fetch Data
  useEffect(() => {
    async function fetchData() {
      try {
        const [jobsRes, appsRes] = await Promise.all([
          api.get("/admin/jobs"),
          api.get("/admin/applications").catch(() => ({ data: [] }))
        ]);
        const jobList = jobsRes.data.jobs || (Array.isArray(jobsRes.data) ? jobsRes.data : []);
        setJobs(jobList);
        setApplications(appsRes.data.applications || []);
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // 1. Update the Filter Logic (line ~76)
  const filteredJobs = jobs.filter((job) => {
    const isActive = job.is_active === true || job.is_active === 1;
    if (tabValue === 1) return isActive;  // Active
    if (tabValue === 2) return !isActive; // Inactive
    return true; // All
  });

  // 3. Pagination Variables
  const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
  const paginatedJobs = filteredJobs.slice(
    (page - 1) * JOBS_PER_PAGE,
    page * JOBS_PER_PAGE
  );

  // 4. Event Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handleEditOpen = (job) => {
    setSelectedJob(job);
    setEditForm({
      title: job.title || "",
      company_name: job.company_name || job.companyName || "",
      location: job.location || "",
      salary: job.salary || "",
      description: job.description || job.job_description || "",
      requirements: Array.isArray(job.requirements) 
        ? job.requirements.join("\n") 
        : (job.requirements || "")
    });
    setOpenEdit(true);
  };

  const handleEditChange = (e) => {
    if (e && e.target) {
      const { name, value } = e.target;
      setEditForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleQuillChange = (value) => {
    setEditForm((prev) => ({ ...prev, description: value }));
  };

  const handleUpdateJob = async () => {
    if (!selectedJob) return;
    try {
      const payload = {
        ...editForm,
        requirements: typeof editForm.requirements === 'string'
          ? editForm.requirements.split("\n").filter(r => r.trim())
          : editForm.requirements
      };
      const response = await api.patch(`/admin/jobs/${selectedJob.id}`, payload);
      setJobs((prevJobs) =>
        prevJobs.map((job) =>
          job.id === selectedJob.id ? { ...job, ...response.data } : job
        )
      );
      setSnackbar({ open: true, message: "Job updated successfully", severity: "success" });
      setOpenEdit(false);
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to update job", severity: "error" });
    }
  };

  const handleDeleteJob = async (id) => {
    if (!id || !window.confirm("Are you sure?")) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      setJobs((prev) => prev.filter((job) => job.id !== id));
      setSnackbar({ open: true, message: "Job deleted successfully", severity: "success" });
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to delete job", severity: "error" });
    }
  };

  const handleToggleStatus = async (job) => {
    try {
      const newStatus = !job.is_active;

      await api.patch(`/admin/jobs/${job.id}`, {
        is_active: newStatus,
      });

      setJobs((prevJobs) =>
        prevJobs.map((j) =>
          j.id === job.id
            ? { ...j, is_active: newStatus }
            : j
        )
      );

      setSnackbar({
        open: true,
        message: `Job marked as ${
          newStatus ? "Active" : "Inactive"
        }`,
        severity: "success",
      });
    } catch (error) {
      console.error(error);

      setSnackbar({
        open: true,
        message: "Failed to update job status",
        severity: "error",
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" sx={{ color: "#4F46E5", fontWeight: "bold" }}>
          Jobs Posted
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/admin/jobs/new")}
          sx={{
            bgcolor: "#4F46E5",
            '&:hover': { bgcolor: "#007a7a" },
            textTransform: 'none',
            fontWeight: 'bold',
            borderRadius: '8px'
          }}
        >
          Post New Job
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
          <Tab label={`All (${jobs.length})`} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
          <Tab label={`Active (${jobs.filter(j => j.is_active === true || j.is_active === 1).length})`} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
          <Tab label={`Inactive (${jobs.filter(j => j.is_active === false || j.is_active === 0).length})`} sx={{ textTransform: 'none', fontWeight: 'bold' }} />
        </Tabs>
      </Box>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid transparent",
          transition: "border-color 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            border: "1px solid #4F46E5",
            boxShadow: "0 8px 20px rgba(79,70,229,0.12)",
          },
          "&:focus-within": {
            border: "1px solid #4F46E5",
            boxShadow: "0 8px 20px rgba(79,70,229,0.12)",
          },
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="jobs table">
          <TableHead sx={{ bgcolor: '#f8f9fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Salary</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedJobs.map((job) => {
              const isActive = job.is_active === true || job.is_active === 1;
              return (
                <TableRow key={job.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>
                    {job.title}
                  </TableCell>
                  <TableCell sx={{ color: '#4F46E5', verticalAlign: 'top' }}>
                    {job.company_name || job.companyName}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {job.location || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {job.salary || job.salary_range || 'N/A'}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300, verticalAlign: "top" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#334155",
                        fontWeight: 500,
                        lineHeight: 1.6,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        wordBreak: "break-word",
                      }}
                    >
                      {(job.job_description || job.description || "No description provided.").replace(/<[^>]*>?/gm, " ")}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Switch
                        checked={isActive}
                        onChange={() => handleToggleStatus(job)}
                        color="success"
                      />

                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isActive ? "#2e7d32" : "#d32f2f",
                        }}
                      >
                        {isActive ? "Active" : "Inactive"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<People />}
                        onClick={() => navigate(`/admin/jobs/${job.id}/candidates`)}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 'bold',
                          color: '#4F46E5',
                          borderColor: '#4F46E5',
                          '&:hover': { borderColor: '#4338CA', bgcolor: 'rgba(79,70,229,0.04)' }
                        }}
                      >
                        Screen Candidates
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleEditOpen(job)}
                        sx={{ color: '#4F46E5' }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteJob(job.id)}
                        sx={{ color: '#d32f2f' }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination count={totalPages} page={page} onChange={(e, v) => setPage(v)} color="primary" />
        </Box>
      )}

      {/* Edit Dialog with all form fields */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
              <DialogTitle>Edit Job Details</DialogTitle>
              <DialogContent dividers>
                <TextField margin="dense" label="Job Title" name="title" fullWidth value={editForm.title} onChange={handleEditChange} />
                <TextField margin="dense" label="Company Name" name="company_name" fullWidth value={editForm.company_name} onChange={handleEditChange} />
                <TextField margin="dense" label="Location" name="location" fullWidth value={editForm.location} onChange={handleEditChange} />
                <TextField margin="dense" label="Salary Range" name="salary" fullWidth value={editForm.salary} onChange={handleEditChange} />
                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>Job Description</Typography>
                  <ReactQuill
                    theme="snow"
                    value={editForm.description}
                    onChange={handleQuillChange}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['clean']
                      ],
                    }}
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </Box>
                <TextField margin="dense" label="Requirements" name="requirements" fullWidth multiline rows={3} value={editForm.requirements} onChange={handleEditChange} sx={{ '& .MuiInputBase-input': { whiteSpace: 'pre-line', wordBreak: 'break-word' } }} />
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                <Button variant="contained" onClick={handleUpdateJob}>Save Changes</Button>
              </DialogActions>
            </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
          </Container>
        );
      }

      export default MyJobs;
